import { CONFLUENT_ALL_STAR_MAPPINGS } from "@/configuration/groups/confluent-all-star/document-mappings";
import {
  CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS,
  getConfluentAllStarEmailRecipients,
} from "@/configuration/groups/confluent-all-star/routing";
import { DocRoutingStrategy } from "@/src/lib/constants";
import { Classification, ClassificationSection } from "@/src/models/constants/classifications";
import { DefaultWorkflow, Workflow, WorkflowType } from "@/src/models/constants/group";
import { Patient } from "@/src/models/patient";
import { DEFAULT_CLASSIFICATIONS_TO_EXTRACT, Section } from "@/src/models/section";

export const CONFLUENT_ALL_STAR_TIMEZONE = "America/Los_Angeles";

const CONFLUENT_ALL_STAR_CLASSIFICATIONS: Classification[] = Array.from(
  new Set<Classification>([
    ...DEFAULT_CLASSIFICATIONS_TO_EXTRACT,
    ...(Object.keys(CONFLUENT_ALL_STAR_MAPPINGS) as Classification[]),
  ])
);

const CONFLUENT_ALL_STAR_NON_EXTRACT_CLASSIFICATIONS = new Set<Classification>([
  Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT,
]);

export function getConfluentAllStarClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classifications",
      options: [...CONFLUENT_ALL_STAR_CLASSIFICATIONS],
    },
  ];
}

export function getConfluentAllStarClassificationToExtract(): Classification[] {
  return CONFLUENT_ALL_STAR_CLASSIFICATIONS.filter(
    (classification) => !CONFLUENT_ALL_STAR_NON_EXTRACT_CLASSIFICATIONS.has(classification)
  );
}

export function getConfluentAllStarWorkflows(): Partial<Record<WorkflowType, Classification[]>> {
  const referralClassifications = DefaultWorkflow[Workflow.REFERRAL] ?? [];

  return {
    ...DefaultWorkflow,
    // Junk is extracted so it surfaces for intake review (rather than silently auto-closing as a
    // no-extract section), but it belongs to no downstream workflow: after intake it is dropped.
    [Workflow.DOC_MANAGEMENT]: getConfluentAllStarClassificationToExtract().filter(
      (classification) => !referralClassifications.includes(classification) && classification !== Classification.JUNK
    ),
  };
}

export function getConfluentAllStarDocRoutingStrategy(section: Section, _patient: Patient): DocRoutingStrategy[] {
  const classification = section.uploadClassification || section.classification;

  if (classification === Classification.JUNK || classification === Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT) {
    return [];
  }

  if (CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS.has(classification)) {
    return [DocRoutingStrategy.EMAIL];
  }

  return [DocRoutingStrategy.EHR];
}

export function getConfluentAllStarDocRoutingEmailRecipients(section: Section, _patient: Patient): string[] {
  return getConfluentAllStarEmailRecipients(section);
}

export function isConfluentAllStarPatientlessDoc(section: Section): boolean {
  // Junk is reviewed at intake only to capture the received date and then dropped: no patient is
  // extracted or looked up, so the section is always patientless and must skip patient resolution.
  return (section.uploadClassification || section.classification) === Classification.JUNK;
}

export function shouldConfluentAllStarForceIntakeReview(): boolean {
  // Every ingested fax must surface a review-needed intake task — no exceptions. Forcing review
  // disables the deterministic "disposition" auto-close, so documents with no extractable
  // classification (e.g. entirely INCOMPLETE_CANNOT_LINK_TO_PATIENT, or a BLANK/UNKNOWN
  // misclassification) escalate to a human instead of silently completing.
  return true;
}
